<template>
  <div>
    <app-spinner v-if="!data_loaded" />
    <template v-else>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__lead><div class="table-default__left">Lead</div></template>
        <template #links="{ row }">
          <div class="table-default__controls">
            <a
              :href="`https://story.altezza.travel/changeInvoice/${row.story_id}`"
              target="_blank"
              class="table-default__activity table-default__activity--itinerary"
              :class="{ 'table-default__activity--active': row.story_id }"
            />
            <a
              :href="`https://story.altezza.travel/invoice/shared/${row.hash}`"
              target="_blank"
              class="table-default__activity table-default__activity--clients"
              :class="{ 'table-default__activity--active': row.hash }"
            />
          </div>
        </template>
        <template #invoice="{ row }">{{ row.number }}</template>
        <template #lead="{ row }">
          <div v-if="row.itinerary?.lead" class="table-default__left table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.itinerary.lead.manager.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.itinerary.lead.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span>{{ row.itinerary.lead.name || '-' }}</span>
          </div>
          <span v-else>-</span>
        </template>
        <template #bank="{ row }">
          <template v-if="row.auth_data_charge_info.pos_transaction">
            {{ row.auth_data_charge_info.pos_transaction.bank.short_name }} ({{ row.auth_data_charge_info.pos_transaction.channel.name }})
          </template>
          <template v-else>-</template>
        </template>
        <template #amount="{ row }">
          <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.amount) }} $</template>
          <template v-else>-</template>
        </template>
        <template #commission="{ row }">
          <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.commission) }} $</template>
          <template v-else>-</template>
        </template>
        <template #settlement="{ row }">
          <template v-if="row.auth_data_charge_info.pos_transaction">{{ formatString(row.auth_data_charge_info.pos_transaction.settlement_amount) }} $</template>
          <template v-else>-</template>
        </template>
        <template #rrn="{ row }">
          <template v-if="row.auth_data_charge_info.pos_transaction">{{ row.auth_data_charge_info.pos_transaction.rrn }}</template>
          <template v-else>-</template>
        </template>
        <template #slip="{ row }">
          <a
            v-if="row.auth_data_charge_info.pos_transaction"
            :href="row.auth_data_charge_info.pos_transaction.machine_slip.file"
            target="_blank"
            class="status-tag"
            style="background-color: #439ad1"
          >
            View
          </a>
          <template v-else>-</template>
        </template>
        <template #charged_by="{ row }">
          <div v-if="row.auth_data_charge_info" class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.auth_data_charge_info.charged_by.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.auth_data_charge_info.charged_by.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span>{{ dateTimeFormat(row.auth_data_charge_info.charged_at) }}</span>
          </div>
          <span v-else>-</span>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getFinanceInvoiceList } from '@/http'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'AuthorizationFormCharged',
  data() {
    return {
      data_loaded: false,
      table: {
        items: [],
        columns: [
          'links',
          'invoice',
          'lead',
          'bank',
          'amount',
          'commission',
          'settlement',
          'rrn',
          'slip',
          'charged_by',
        ],
        options: {
          headings: {
            'links': 'Links',
            'invoice': 'Invoice',
            'lead': 'Lead',
            'bank': 'Bank (Channel)',
            'amount': 'Charged amount',
            'rrn': 'RRN',
            'commission': 'Commission',
            'settlement': 'Settlement',
            'slip': 'Slip',
            'charged_by': 'Charged by',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      getFinanceInvoiceList({
        page: this.pagination.page,
        page_size: 40,
        authorization_form_completed: true,
        authorization_form_charged: true,
        ordering: '-auth_data_charge_info__charged_at',
        status: 'paid'
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.data_loaded = true
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('ru-RU').format(string) : '-'
    },
  }
}
</script>

<style lang="sass" scoped>
.form-signature
  display: block
  max-height: 120px
</style>
